import Dexie from "dexie";
import { RESOURCE_TYPES } from "@edu-quizz/shared/constants/resourceTypes";
import {
    saveFlashcardDecks,
    getFlashcardsByDeck as getFlashcardsByDeckInternal,
    getFlashcardDeck as getFlashcardDeckInternal,
    getFlashcardDeckWithCards as getFlashcardDeckWithCardsInternal,
} from "./resources/flashcard.js";
import {
    saveQuizzes,
    getQuiz as getQuizInternal,
    getQuestionsByQuiz as getQuestionsByQuizInternal,
    getQuestionOptions as getQuestionOptionsInternal,
    getQuizWithQuestions as getQuizWithQuestionsInternal,
} from "./resources/quiz.js";
import {
    saveArticles,
    saveMyArticles,
    getArticle as getArticleInternal,
    getArticleResources as getArticleResourcesInternal,
    getArticleWithAttachedResources as getArticleWithAttachedResourcesInternal,
} from "./resources/article.js";
import {
    saveBookmarks,
    getBookmark as getBookmarkInternal,
} from "./resources/bookmark.js";
import {
    saveTocs,
    getToc as getTocInternal,
} from "./resources/toc.js";
import { saveOfflineResources as saveOfflineResourcesInternal } from "./saveOfflineResources.js";
import { getSchoolsHierarchy as getSchoolsHierarchyInternal } from "./schools.js";

/**
 * QuizClasse Local Database
 * Stores learning resources, user progress, and sync queue offline
 */
export class EduQuizDB extends Dexie {
    constructor() {
        super("EduQuizDB");

        // Define database schema
        this.version(1).stores({
            // Authentication
            auth: "id, lastSync, name, rememberMe",

            // Flashcard cards (individual cards within a deck)
            flashcardsCards: "serverId, deckServerResourceId, front, back",

            // Flashcard decks
            flashcardDecks: "serverResourceId, name",

            // Quizzes
            quizzes: "serverResourceId, name",

            // Quiz questions
            quizQuestions:
                "serverId, quizServerResourceId, questionText, questionType, order",

            // Quiz question options
            quizQuestionOptions:
                "serverId, questionServerId, optionText, isCorrect, order",

            // Bookmarks (videos and websites)
            bookmarks: "serverResourceId, type, name, url",

            // Articles
            articles:
                "serverResourceId, resourceId, content, name, description",

            // Article resources (pivot table for article-to-resource relationships)
            articleResources: "[articleServerResourceId+serverResourceId], articleServerResourceId, serverResourceId, order",

            // User progress (local, synced later)
            progress: "uuid, [resourceId+type], resourceId, type, completed, score, lastAttempt",

            // Flashcard reviews
            flashcardReviews:
                "uuid, flashcardCardServerId, quality, reviewedAt, nextReview",

            // Quiz answers
            quizAnswers:
                "uuid, quizResourceServerId, questionServerId, selectedOptionIds, answerText, isCorrect, answeredAt",

            // Sync queue for offline changes
            syncQueue:
                "++id, uuid, action, resourceType, resourceId, data, timestamp, retryCount, targetLocalTableName, targetLocalRowUuid",

            // Schools
            schools: "serverId, name",

            // Classrooms
            classrooms: "serverId, schoolId, name",

            // Resource assignments
            resourceAssignments:
                "serverId, classroomId, resourceType, resourceId",

            // My Resources (user's own resources)
            myResources: "[resourceId+resourceType], resourceId, resourceType",
        });

        // Version 2: Add study_both_sides field to flashcard cards
        this.version(2).stores({
            // Keep all existing stores unchanged, Dexie will maintain them
            flashcardsCards: "serverId, deckServerResourceId, front, back, studyBothSides",
        });

        // Version 3: Add theme configuration table (deprecated, use settings instead)
        this.version(3).stores({
            // Theme configuration for customizable colors
            theme: "id, primaryColorStart, primaryColorEnd, accentColorStart, accentColorEnd",
        });

        // Version 4: Add settings table for all app preferences
        this.version(4).stores({
            // Generic settings table (key-value store)
            settings: "key, value",
        });

        // Version 5: Add fullyLearned field to flashcard reviews
        this.version(5).stores({
            // Add fullyLearned to track if a flashcard has been fully mastered
            flashcardReviews:
                "uuid, flashcardCardServerId, quality, reviewedAt, nextReview, fullyLearned",
        });

        // Version 6: Add playMode field to flashcard reviews
        this.version(6).stores({
            // Add playMode to track if review was done in 'spaced' or 'intensive' mode
            flashcardReviews:
                "uuid, flashcardCardServerId, quality, reviewedAt, nextReview, fullyLearned, playMode",
        });

        // Version 7: Add images table and image references to flashcard cards
        this.version(7).stores({
            // Images table to store all resource images
            images: "uuid, url, shouldDelete, urlUpdatedAt",
            // Update flashcard cards to include image UUID arrays
            flashcardsCards: "serverId, deckServerResourceId, front, back, studyBothSides, questionImageUuids, answerImageUuids",

            syncAttemptInfo: "++id, attemptDate, status, errorMessage",
        });

        // Version 8: Add questionAudioUuid field to flashcard cards
        this.version(8).stores({
            flashcardsCards: "serverId, deckServerResourceId, front, back, studyBothSides, questionImageUuids, answerImageUuids, questionAudioUuid",
        });

        // Version 9: Rename images table to assets and add type field
        this.version(9).stores({
            assets: "uuid, url, type, shouldDelete, urlUpdatedAt",
            images: null, // Delete old images table
        });

        // Version 10: Add optionTextMatch field to quiz question options for match questions
        this.version(10).stores({
            quizQuestionOptions:
                "serverId, questionServerId, optionText, optionTextMatch, isCorrect, order",
        });

        // Version 12: Add hotspotZone field to quiz question options
        this.version(12).stores({
            quizQuestionOptions:
                "serverId, questionServerId, optionText, optionTextMatch, isCorrect, order, hotspotZone",
        });

        // Version 13: Add table of contents tables
        this.version(13).stores({
            // Root TOC record (one per resource)
            tableOfContents: "serverResourceId, name",
            // Themes within a TOC
            tocThemes: "serverId, tocServerResourceId, name, order",
            // Parts within a theme
            tocParts: "serverId, themeServerId, name, order",
            // Chapters within a part
            tocChapters: "serverId, partServerId, name, order",
            // Resources assigned to a chapter
            tocChapterResources: "[chapterServerId+resourceServerId], chapterServerId, resourceServerId, resourceType, order",
        });

        // Version 11: Add questionImageUuids field to quiz questions
        this.version(11).stores({
            quizQuestions:
                "serverId, quizServerResourceId, questionText, questionType, order",
        }).upgrade(trans => {
            return trans.table("quizQuestions").toCollection().modify(q => {
                if (q.questionImageUuids === undefined) {
                    q.questionImageUuids = [];
                }
            });
        });

        // Define table references for TypeScript-like autocomplete
        this.auth = this.table("auth");
        this.flashcardsCards = this.table("flashcardsCards");
        this.flashcardDecks = this.table("flashcardDecks");
        this.quizzes = this.table("quizzes");
        this.quizQuestions = this.table("quizQuestions");
        this.quizQuestionOptions = this.table("quizQuestionOptions");
        this.bookmarks = this.table("bookmarks");
        this.articles = this.table("articles");
        this.articleResources = this.table("articleResources");
        this.progress = this.table("progress");
        this.flashcardReviews = this.table("flashcardReviews");
        this.quizAnswers = this.table("quizAnswers");
        this.syncQueue = this.table("syncQueue");
        this.schools = this.table("schools");
        this.classrooms = this.table("classrooms");
        this.resourceAssignments = this.table("resourceAssignments");
        this.myResources = this.table("myResources");
        this.theme = this.table("theme");
        this.settings = this.table("settings");
        this.assets = this.table("assets");
        this.syncAttemptInfo = this.table("syncAttemptInfo");
        this.tableOfContents = this.table("tableOfContents");
        this.tocThemes = this.table("tocThemes");
        this.tocParts = this.table("tocParts");
        this.tocChapters = this.table("tocChapters");
        this.tocChapterResources = this.table("tocChapterResources");
    }

    /**
     * Get theme configuration
     */
    async getTheme() {
        // Try new settings table first, fallback to old theme table
        const themeFromSettings = await this.getSetting('theme');
        if (themeFromSettings) {
            return themeFromSettings;
        }

        // Fallback to old theme table for backwards compatibility
        const oldTheme = await this.theme.get(1);
        if (oldTheme) {
            // Migrate to new settings table
            await this.saveSetting('theme', oldTheme);
            return oldTheme;
        }

        return null;
    }

    /**
     * Save theme configuration
     */
    async saveTheme(primaryColorStart, primaryColorEnd, accentColorStart, accentColorEnd) {
        const themeData = {
            primaryColorStart,
            primaryColorEnd,
            accentColorStart,
            accentColorEnd,
        };
        return await this.saveSetting('theme', themeData);
    }

    /**
     * Update theme configuration
     */
    async updateTheme(themeData) {
        return await this.saveSetting('theme', themeData);
    }

    /**
     * Get a setting value by key
     */
    async getSetting(key) {
        const result = await this.settings.get(key);
        return result ? result.value : null;
    }

    /**
     * Save a setting value
     */
    async saveSetting(key, value) {
        const existing = await this.settings.get(key);
        if (existing) {
            return await this.settings.update(key, { value });
        } else {
            return await this.settings.add({ key, value });
        }
    }

    /**
     * Delete a setting
     */
    async deleteSetting(key) {
        return await this.settings.delete(key);
    }

    /**
     * Get all settings
     */
    async getAllSettings() {
        const settingsArray = await this.settings.toArray();
        const settingsMap = {};
        settingsArray.forEach((setting) => {
            settingsMap[setting.key] = setting.value;
        });
        return settingsMap;
    }

    /**
     * Get dark mode preference
     */
    async getDarkMode() {
        return await this.getSetting('darkMode');
    }

    /**
     * Save dark mode preference
     */
    async saveDarkMode(isDark) {
        return await this.saveSetting('darkMode', isDark);
    }

    /**
     * Get font preference
     */
    async getFontPreference() {
        return await this.getSetting('fontPreference');
    }

    /**
     * Save font preference
     */
    async saveFontPreference(fontName) {
        return await this.saveSetting('fontPreference', fontName);
    }

    /**
     * Save authentication data
     */
    async saveAuth(code, user, rememberMe = false) {
        await this.auth.clear();
        const authData = {
            id: 1,
            name: user?.name,
            lastSync: new Date().toISOString(),
            rememberMe: rememberMe,
        };

        console.log("saveAuth user", user, user?.name, authData);

        await this.auth.add(authData);
    }

    /**
     * Get authentication data
     */
    async getAuth() {
        return await this.auth.get(1);
    }

    /**
     * Check if user is authenticated
     */
    async isAuthenticated() {
        const auth = await this.getAuth();
        return !!auth && !!auth.code;
    }

    /**
     * Clear all user data (logout)
     */
    async clearAll() {
        await Promise.all([
            this.auth.clear(),
            this.flashcardsCards.clear(),
            this.flashcardDecks.clear(),
            this.quizzes.clear(),
            this.quizQuestions.clear(),
            this.quizQuestionOptions.clear(),
            this.bookmarks.clear(),
            this.articles.clear(),
            this.articleResources.clear(),
            this.progress.clear(),
            this.flashcardReviews.clear(),
            this.quizAnswers.clear(),
            this.syncQueue.clear(),
            this.schools.clear(),
            this.classrooms.clear(),
            this.resourceAssignments.clear(),
            this.myResources.clear(),
            this.assets.clear(),
            this.syncAttemptInfo.clear(),
            this.tableOfContents.clear(),
            this.tocThemes.clear(),
            this.tocParts.clear(),
            this.tocChapters.clear(),
            this.tocChapterResources.clear(),
        ]);

        // Clear assets cache
        if ('caches' in window) {
            await caches.delete('assets-cache-v1.0.1').catch(err =>
                console.warn("Failed to clear assets cache:", err)
            );
        }
    }

    /**
     * Add item to sync queue
     */
    async addToSyncQueue(action, resourceType, resourceId, data, targetLocalTableName = null, targetLocalRowUuid = null) {
        // Generate a UUID v4
        const uuid = crypto.randomUUID();

        return await this.syncQueue.add({
            uuid,
            action, // 'create', 'update', 'delete'
            resourceType, // Resource type from RESOURCE_TYPES (quiz, flashcard_deck, etc.)
            resourceId,
            data,
            timestamp: new Date().toISOString(),
            retryCount: 0,
            targetLocalTableName, // Target local table name (e.g., 'flashcardReviews')
            targetLocalRowUuid, // Target local row UUID
        });
    }

    /**
     * Get pending sync items
     */
    async getPendingSyncItems() {
        return await this.syncQueue.toArray();
    }

    /**
     * Remove item from sync queue
     */
    async removeFromSyncQueue(id) {
        return await this.syncQueue.delete(id);
    }

    /**
     * Increment retry count for sync item
     */
    async incrementSyncRetry(id) {
        const item = await this.syncQueue.get(id);
        if (item) {
            await this.syncQueue.update(id, {
                retryCount: item.retryCount + 1,
            });
        }
    }

    /**
     * Increment retry count for sync item and delete if retry count exceeds limit
     * @param {number} id - Sync item ID
     * @param {number} retryLimit - Maximum number of retries (default: 3)
     * @returns {Promise<boolean>} - Returns true if item was deleted, false otherwise
     */
    async incrementSyncRetryWithLimitRetry(id, retryLimit = 3) {
        const item = await this.syncQueue.get(id);
        if (item) {
            const newRetryCount = item.retryCount + 1;
            if (newRetryCount > retryLimit) {
                console.warn(
                    `Sync item ${id} exceeded retry limit (${newRetryCount} retries), removing from queue`,
                );
                await this.syncQueue.delete(id);
                return true;
            } else {
                await this.syncQueue.update(id, {
                    retryCount: newRetryCount,
                });
                return false;
            }
        }
        return false;
    }

    /**
     * Save user progress
     */
    async saveProgress(resourceId, type, completed, score) {
        const existing = await this.progress
            .where(["resourceId", "type"])
            .equals([resourceId, type])
            .first();

        const progressData = {
            resourceId,
            type,
            completed,
            score,
            lastAttempt: new Date().toISOString(),
        };

        if (existing) {
            await this.progress.update(existing.uuid, progressData);
        } else {
            // Generate UUID for new progress record
            const uuid = crypto.randomUUID();
            progressData.uuid = uuid;

            await this.progress.add(progressData);
        }
    }

    /**
     * Get progress for a resource
     */
    async getProgress(resourceId, type) {
        return await this.progress
            .where(["resourceId", "type"])
            .equals([resourceId, type])
            .first();
    }

    /**
     * Get all progress
     */
    async getAllProgress() {
        return await this.progress.toArray();
    }

    /**
     * Save offline resources (schools, classrooms, resource assignments)
     */
    async saveOfflineResources(offlineData) {
        return saveOfflineResourcesInternal(this, offlineData);
    }

    /**
     * Get all schools with their classrooms and resources
     */
    async getSchoolsHierarchy() {
        return getSchoolsHierarchyInternal(this);
    }

    /**
     * Get flashcards for a specific deck
     */
    async getFlashcardsByDeck(deckId) {
        return getFlashcardsByDeckInternal(this, deckId);
    }

    /**
     * Get a flashcard deck by server resource ID
     */
    async getFlashcardDeck(serverResourceId) {
        return getFlashcardDeckInternal(this, serverResourceId);
    }

    /**
     * Get a flashcard deck with its cards
     */
    async getFlashcardDeckWithCards(serverResourceId) {
        return getFlashcardDeckWithCardsInternal(this, serverResourceId);
    }

    /**
     * Get a resource assignment by ID (includes full resource data)
     */
    async getResourceAssignment(assignmentId) {
        try {
            return await this.resourceAssignments
                .where("serverId")
                .equals(assignmentId)
                .first();
        } catch (error) {
            console.error("Failed to get resource assignment:", error);
            throw error;
        }
    }

    /**
     * Get a quiz by server resource ID
     */
    async getQuiz(serverResourceId) {
        return getQuizInternal(this, serverResourceId);
    }

    /**
     * Get questions for a specific quiz
     */
    async getQuestionsByQuiz(quizId) {
        return getQuestionsByQuizInternal(this, quizId);
    }

    /**
     * Get options for a specific question
     */
    async getQuestionOptions(questionId) {
        return getQuestionOptionsInternal(this, questionId);
    }

    /**
     * Get a quiz with its questions and options
     */
    async getQuizWithQuestions(serverResourceId) {
        return getQuizWithQuestionsInternal(this, serverResourceId);
    }

    /**
     * Get a bookmark by server resource ID
     */
    async getBookmark(serverResourceId) {
        return getBookmarkInternal(this, serverResourceId);
    }

    /**
     * Get an article by server resource ID
     */
    async getArticle(serverResourceId) {
        return getArticleInternal(this, serverResourceId);
    }

    /**
     * Get attached resources for a specific article
     */
    async getArticleResources(articleId) {
        return getArticleResourcesInternal(this, articleId);
    }

    /**
     * Get an article with its attached resources
     */
    async getArticleWithAttachedResources(serverResourceId) {
        return getArticleWithAttachedResourcesInternal(this, serverResourceId);
    }

    /**
     * Get a table of contents with full hierarchy by server resource ID
     */
    async getToc(serverResourceId) {
        return getTocInternal(this, serverResourceId);
    }

    /**
     * Get an asset by UUID
     */
    async getAsset(uuid) {
        return await this.assets.get(uuid);
    }

    /**
     * Get multiple assets by UUIDs
     */
    async getAssets(uuids) {
        if (!uuids || uuids.length === 0) return [];
        return await this.assets.where("uuid").anyOf(uuids).toArray();
    }

    /**
     * Get multiple assets by UUIDs and type
     */
    async getAssetsByType(uuids, type) {
        if (!uuids || uuids.length === 0) return [];
        const assets = await this.assets.where("uuid").anyOf(uuids).toArray();
        return assets.filter(asset => asset.type === type);
    }

    /**
     * Add a sync attempt to the log
     * @param {string} status - Status of the sync attempt (e.g., 'success', 'error', 'in_progress')
     * @param {string|null} errorMessage - Error message if the sync failed
     * @returns {Promise<number>} - Returns the ID of the created sync attempt
     */
    async addSyncAttempt(status, errorMessage = null) {
        return await this.syncAttemptInfo.add({
            attemptDate: new Date().toISOString(),
            status,
            errorMessage,
        });
    }

    /**
     * Update a sync attempt status
     * @param {number} id - ID of the sync attempt
     * @param {string} status - New status
     * @param {string|null} errorMessage - Error message if applicable
     */
    async updateSyncAttempt(id, status, errorMessage = null) {
        return await this.syncAttemptInfo.update(id, {
            status,
            errorMessage,
        });
    }

    /**
     * Get all sync attempts
     * @returns {Promise<Array>} - Array of all sync attempts
     */
    async getAllSyncAttempts() {
        return await this.syncAttemptInfo.toArray();
    }

    /**
     * Get the latest sync attempt
     * @returns {Promise<Object|null>} - Latest sync attempt or null
     */
    async getLatestSyncAttempt() {
        return await this.syncAttemptInfo.orderBy("id").reverse().first();
    }

    /**
     * Get sync attempts with a specific status
     * @param {string} status - Status to filter by
     * @returns {Promise<Array>} - Array of sync attempts with the given status
     */
    async getSyncAttemptsByStatus(status) {
        return await this.syncAttemptInfo.where("status").equals(status).toArray();
    }

    /**
     * Delete old sync attempts (keep only the last N attempts)
     * @param {number} keepCount - Number of recent attempts to keep (default: 100)
     */
    async cleanupOldSyncAttempts(keepCount = 100) {
        const allAttempts = await this.syncAttemptInfo
            .orderBy("id")
            .reverse()
            .toArray();

        if (allAttempts.length > keepCount) {
            const idsToDelete = allAttempts
                .slice(keepCount)
                .map((attempt) => attempt.id);

            await this.syncAttemptInfo.bulkDelete(idsToDelete);
            return idsToDelete.length;
        }

        return 0;
    }

    /**
     * Get assets marked for deletion
     * @returns {Promise<Array>} - Array of assets that should be deleted
     */
    async getAssetsToDelete() {
        return await this.assets.filter(asset => asset.shouldDelete === true).toArray();
    }

    /**
     * Delete assets marked for deletion
     * @returns {Promise<number>} - Number of assets deleted
     */
    async cleanupMarkedAssets() {
        const assetsToDelete = await this.getAssetsToDelete();
        const uuids = assetsToDelete.map(asset => asset.uuid);

        if (uuids.length > 0) {
            await this.assets.bulkDelete(uuids);
            console.log(`Cleaned up ${uuids.length} marked assets from database`);
        }

        return uuids.length;
    }

    /**
     * Mark all assets for deletion (used before sync to mark old assets)
     */
    async markAllAssetsForDeletion() {
        await this.assets.toCollection().modify({ shouldDelete: true });
    }

    /**
     * Save or update an asset
     * @param {string} uuid - Asset UUID
     * @param {string} url - Temporary URL
     * @param {string} extension - File extension
     * @param {string} type - Asset type ('image' or 'audio')
     * @param {string} urlCachePWA - PWA cache URL
     */
    async saveAsset(uuid, url, extension, type, urlCachePWA) {
        const existing = await this.assets.get(uuid);
        const now = new Date().toISOString();

        const assetData = {
            uuid,
            url,
            extension: extension || '',
            type: type || 'image',
            urlCachePWA: extension ? urlCachePWA : null,
            shouldDelete: false,
            urlUpdatedAt: now,
        };

        if (existing) {
            await this.assets.update(uuid, assetData);
        } else {
            await this.assets.add(assetData);
        }
    }
}

// Create and export a singleton instance
export const db = new EduQuizDB();

// Helpers for common operations

/**
 * Initialize database and check authentication
 */
export async function initDB() {
    try {
        await db.open();
        const isAuth = await db.isAuthenticated();
        console.log("Database initialized. Authenticated:", isAuth);
        return isAuth;
    } catch (error) {
        console.error("Failed to initialize database:", error);
        throw error;
    }
}

/**
 * Logout
 */
export async function logout() {
    try {
        await db.clearAll();
        console.log("Logout successful");
        return true;
    } catch (error) {
        console.error("Logout failed:", error);
        return false;
    }
}
